// Preset seeding library. Maps any of the 16 ROOF_TYPES to one or more
// initial RoofSection objects. Presets are STARTING POINTS only — users
// add/remove unlimited sections after seeding to model any geometry.
import { ROOF_TYPES } from "./roofTypes";

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `sec-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const base = (over = {}) => ({
  id: uid(),
  name: "Section",
  roof_type: "gable-to-gable",
  length: 8,
  eave_to_ridge: 4.5,
  pitch: 35,
  overhang: 0.3,
  slope_coverage: "both",
  ridge_length: null,
  hip_length: null,
  valley_length: null,
  eaves_length: null,
  verge_length: null,
  abutment_length: null,
  rear_pitch: null,
  rear_eave_to_ridge: null,
  material_category: "Concrete Tiles",
  coverings: [],
  features: [],
  wind_zone: null,
  exposure: null,
  batten_gauge_mm: null,
  fixing_spec: null,
  ventilation_spec: null,
  waste_pct_override: null,
  // BS EN 1991-1-4 advanced inputs (defaults applied at backend)
  building_height_m: null,
  terrain: null,
  topography_factor: null,
  orography: null,
  c_direction: null,
  c_season: null,
  safety_factor: null,
  ai_generated: false,
  ai_confidence: null,
  ai_source: null,
  ai_review_status: null,
  overrides: [],
  ...over,
});

// Per-preset seeding logic. Each returns an ARRAY of sections (users can
// then add more). The seeds use reasonable UK domestic defaults so the
// quote is immediately calculable, but every value is overridable.
const SEEDERS = {
  "gable-to-gable": () => [base({ name: "Main gable", roof_type: "gable-to-gable" })],

  "hip-to-hip": () => [base({ name: "Main hip", roof_type: "hip-to-hip", length: 9, eave_to_ridge: 4.2 })],

  "mono-pitch": () => [base({ name: "Mono slope", roof_type: "mono-pitch", slope_coverage: "front", pitch: 15, eave_to_ridge: 5.5 })],

  "lean-to": () => [base({ name: "Lean-to", roof_type: "lean-to", slope_coverage: "front", pitch: 20, length: 5, eave_to_ridge: 3.5 })],

  flat: () => [base({ name: "Flat roof", roof_type: "flat", slope_coverage: "both", pitch: 2, eave_to_ridge: 3, material_category: "EPDM" })],

  "l-shaped": () => [
    base({ name: "Main arm", roof_type: "gable-to-gable", length: 9, eave_to_ridge: 4.5 }),
    base({ name: "Side arm", roof_type: "gable-to-gable", length: 5, eave_to_ridge: 3.6 }),
  ],

  "t-shaped": () => [
    base({ name: "Front arm", roof_type: "gable-to-gable", length: 10, eave_to_ridge: 4.5 }),
    base({ name: "Rear arm", roof_type: "gable-to-gable", length: 6, eave_to_ridge: 3.8 }),
  ],

  intersecting: () => [
    base({ name: "Primary span", roof_type: "gable-to-gable", length: 10, eave_to_ridge: 4.5 }),
    base({ name: "Cross span", roof_type: "gable-to-gable", length: 6, eave_to_ridge: 3.8 }),
  ],

  dormer: () => [
    base({
      name: "Main roof",
      roof_type: "gable-to-gable",
      length: 9,
      eave_to_ridge: 5.0,
      features: [{ id: uid(), type: "dormer", label: "Front dormer", count: 1, width: 1.6, height: 1.4, length: 2.0 }],
    }),
  ],

  mansard: () => [
    base({ name: "Mansard lower", roof_type: "mansard", length: 10, eave_to_ridge: 3.0, pitch: 70, slope_coverage: "both" }),
    base({ name: "Mansard upper", roof_type: "mansard", length: 10, eave_to_ridge: 3.5, pitch: 30, slope_coverage: "both" }),
  ],

  "dutch-barn": () => [base({ name: "Dutch barn", roof_type: "dutch-barn", length: 12, eave_to_ridge: 5.0, pitch: 30 })],

  "unequal-pitch": () => [
    base({
      name: "Unequal-pitch roof",
      roof_type: "unequal-pitch",
      length: 9,
      eave_to_ridge: 4.5,
      pitch: 35,
      rear_eave_to_ridge: 3.2,
      rear_pitch: 25,
    }),
  ],

  valley: () => [
    base({
      name: "Valley roof",
      roof_type: "valley",
      length: 9,
      eave_to_ridge: 4.5,
      features: [{ id: uid(), type: "valley", label: "Lead valley", count: 1, length: 3.5 }],
    }),
  ],

  parapet: () => [base({ name: "Parapet roof", roof_type: "parapet", pitch: 2, eave_to_ridge: 3, slope_coverage: "both", material_category: "EPDM" })],

  "stepped-terraces": () => [
    base({ name: "Step 1", roof_type: "flat", pitch: 2, length: 6, eave_to_ridge: 3, material_category: "EPDM" }),
    base({ name: "Step 2", roof_type: "flat", pitch: 2, length: 6, eave_to_ridge: 3, material_category: "EPDM" }),
  ],

  complex: () => [
    base({ name: "Main span", roof_type: "hip-to-hip", length: 10, eave_to_ridge: 4.5 }),
    base({ name: "Cross gable", roof_type: "gable-to-gable", length: 5, eave_to_ridge: 3.5,
      features: [{ id: uid(), type: "valley", label: "Cross-gable valley", count: 2, length: 3.5 }] }),
  ],
};

export function seedSectionsForRoofType(roofTypeId) {
  const seeder = SEEDERS[roofTypeId];
  return seeder ? seeder() : SEEDERS["gable-to-gable"]();
}

export function newBlankSection(name = "New section") {
  return base({ name });
}

export function newFeature(type = "valley") {
  return {
    id: uid(),
    type,
    label: "",
    count: 1,
    width: null,
    height: null,
    length: null,
    area: null,
    position: null,
    notes: null,
    material_id: null,
    unit_cost: null,
    labour_hours: null,
    ai_generated: false,
    ai_confidence: null,
  };
}

export function newCovering() {
  return {
    id: uid(),
    material_id: null,
    name: "",
    manufacturer: "",
    category: "",
    area_pct: 100,
    waste_pct_override: null,
    notes: "",
  };
}

export const FEATURE_TYPES = [
  { id: "valley", label: "Valley" },
  { id: "hip", label: "Hip" },
  { id: "abutment", label: "Abutment / wall" },
  { id: "flashing", label: "Flashing" },
  { id: "soaker", label: "Soaker" },
  { id: "velux", label: "Velux / rooflight" },
  { id: "dormer", label: "Dormer" },
  { id: "chimney", label: "Chimney" },
  { id: "solar_pv", label: "Solar PV panel" },
  { id: "gully", label: "Gully" },
  { id: "downpipe", label: "Downpipe" },
];

export const COVERAGE_OPTIONS = [
  { id: "both", label: "Both slopes" },
  { id: "front", label: "Front slope only" },
  { id: "rear", label: "Rear slope only" },
];

export const COVERING_CATEGORIES = [
  { id: "Concrete Tiles", label: "Concrete tiles" },
  { id: "Clay Tiles", label: "Clay tiles / pantiles" },
  { id: "Natural Slate", label: "Natural slate" },
  { id: "Fibre Cement Slate", label: "Fibre cement slate" },
  { id: "Standing Seam", label: "Metal standing seam" },
  { id: "Corrugated Sheeting", label: "Corrugated metal" },
  { id: "EPDM", label: "EPDM single-ply" },
  { id: "GRP Fibreglass", label: "GRP fibreglass" },
  { id: "Flat Roofing", label: "Torch-on felt / built-up" },
  { id: "Leadwork", label: "Lead (valleys, flashings)" },
];

export const WIND_ZONES = ["Zone 1", "Zone 2", "Zone 3", "Zone 4", "Zone 5"];
export const EXPOSURE_OPTIONS = ["sheltered", "moderate", "severe"];

// Validation: list available presets in roof-types order
export const ROOF_PRESET_IDS = ROOF_TYPES.map((r) => r.id);
